import API_BASE from '../config';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = API_BASE;

const INDUSTRIES = [
  'FinTech', 'B2B SaaS', 'Pharma / Healthcare', 'FMCG / Consumer Goods',
  'Manufacturing', 'Banking / BFSI', 'Consulting', 'E-commerce', 'IT Services', 'Other'
];

const SENIORITY_LEVELS = ['Junior', 'Mid-level', 'Senior', 'Manager / Lead', 'Director', 'VP', 'C-Suite'];

const colors = {
  primary: '#1A6FD6',
  navy: '#0D1F3C',
  body: '#6B7280',
  border: '#E5E9F0',
  surface: '#FFFFFF',
  background: 'linear-gradient(135deg, #E8F1FC 0%, #F8FAFF 50%, #FFFFFF 100%)',
};

const RecruiterOnboarding = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    linkedin_url: '',
    years_experience: '',
    industries: [],
    seniority_levels: [],
    why_join: ''
  });

  const toggle = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.title.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    if (form.industries.length === 0) {
      setError('Please select at least one industry you specialise in.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/recruiters/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: colors.background }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');`}</style>
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#D1FAE5' }}>
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24"><path stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
          </div>
          <h1 className="text-2xl font-bold mb-3" style={{ color: colors.navy, fontFamily: 'Fraunces, serif' }}>Application received!</h1>
          <p className="mb-6" style={{ color: colors.body }}>
            Thank you for applying to become a Successlane recruiter partner. Our team will review your application and reach out within 48 hours.
          </p>
          <button onClick={() => navigate('/')} className="text-sm underline" style={{ color: colors.body }}>Back to home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: colors.background, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');`}</style>

      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-6 cursor-pointer" onClick={() => navigate('/')}>
            <svg viewBox="0 0 16 16" className="w-7 h-7" fill="none" stroke={colors.primary} strokeWidth="2">
              <polyline points="2,10 8,3 14,10 8,15"/>
            </svg>
            <span className="text-xl font-bold" style={{ color: colors.navy, fontFamily: 'Fraunces, serif' }}>Successlane</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: colors.navy, fontFamily: 'Fraunces, serif' }}>
            Become a Recruiter Partner
          </h1>
          <p style={{ color: colors.body }}>
            Join our network of senior recruiters who certify LinkedIn profiles for job seekers. Earn per review, work flexibly.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl p-8 border" style={{ borderColor: colors.border }}>
          <form onSubmit={handleSubmit} className="space-y-5">

            {error && (
              <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: colors.navy }}>Full name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Priya Sharma"
                  className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1A6FD6]"
                  style={{ borderColor: colors.border }}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: colors.navy }}>Email address *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="priya@company.com"
                  className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1A6FD6]"
                  style={{ borderColor: colors.border }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: colors.navy }}>WhatsApp number</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1A6FD6]"
                  style={{ borderColor: colors.border }}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: colors.navy }}>Years of recruiting experience</label>
                <input
                  type="text"
                  value={form.years_experience}
                  onChange={e => setForm({ ...form, years_experience: e.target.value })}
                  placeholder="e.g. 8 years"
                  className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1A6FD6]"
                  style={{ borderColor: colors.border }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: colors.navy }}>Current company *</label>
                <input
                  type="text"
                  value={form.company}
                  onChange={e => setForm({ ...form, company: e.target.value })}
                  placeholder="Razorpay, Infosys, etc."
                  className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1A6FD6]"
                  style={{ borderColor: colors.border }}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: colors.navy }}>Your title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="Senior Talent Acquisition Lead"
                  className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1A6FD6]"
                  style={{ borderColor: colors.border }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: colors.navy }}>LinkedIn profile URL</label>
              <input
                type="url"
                value={form.linkedin_url}
                onChange={e => setForm({ ...form, linkedin_url: e.target.value })}
                placeholder="https://linkedin.com/in/yourname"
                className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1A6FD6]"
                style={{ borderColor: colors.border }}
              />
            </div>

            {/* Industries */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: colors.navy }}>
                Industries you specialise in *
              </label>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map(ind => (
                  <button
                    key={ind}
                    type="button"
                    onClick={() => toggle('industries', ind)}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                    style={{
                      backgroundColor: form.industries.includes(ind) ? '#1A6FD6' : '#F1F5F9',
                      color: form.industries.includes(ind) ? '#fff' : '#475569',
                      border: `1px solid ${form.industries.includes(ind) ? '#1A6FD6' : '#E2E8F0'}`
                    }}
                  >
                    {ind}
                  </button>
                ))}
              </div>
            </div>

            {/* Seniority */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: colors.navy }}>
                Seniority levels you hire for
              </label>
              <div className="flex flex-wrap gap-2">
                {SENIORITY_LEVELS.map(lvl => (
                  <button
                    key={lvl}
                    type="button"
                    onClick={() => toggle('seniority_levels', lvl)}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                    style={{
                      backgroundColor: form.seniority_levels.includes(lvl) ? '#0D1F3C' : '#F1F5F9',
                      color: form.seniority_levels.includes(lvl) ? '#fff' : '#475569',
                      border: `1px solid ${form.seniority_levels.includes(lvl) ? '#0D1F3C' : '#E2E8F0'}`
                    }}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </div>

            {/* Why join */}
            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: colors.navy }}>
                Why do you want to partner with Successlane? <span style={{ color: colors.body, fontWeight: '400' }}>(optional)</span>
              </label>
              <textarea
                rows={3}
                value={form.why_join}
                onChange={e => setForm({ ...form, why_join: e.target.value })}
                placeholder="Tell us a bit about your experience and what kind of candidates you typically hire..."
                className="w-full px-3 py-2.5 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1A6FD6]"
                style={{ borderColor: colors.border }}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 rounded-xl font-bold text-white text-base disabled:opacity-60 transition-all hover:-translate-y-px"
              style={{ backgroundColor: colors.primary, boxShadow: '0 4px 20px rgba(26,111,214,0.35)' }}
            >
              {submitting ? 'Submitting…' : 'Submit Application →'}
            </button>

            <p className="text-xs text-center" style={{ color: colors.body }}>
              We review all applications within 48 hours. Successful applicants will be contacted on WhatsApp.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RecruiterOnboarding;
